import {
  accuracyFromWinPercents,
  uciLineToSan,
  classifyMove,
  isSacrifice,
  scoreToWinPercent,
  type EngineScore,
  type MoveCategory,
  type ParsedGame,
} from "@onchainreps/chess";
import type { EngineResult } from "@onchainreps/engine";

export interface EngineLineView {
  san: string[];
  score: EngineScore | null;
  depth: number;
}

export interface PositionAnalysis {
  ply: number;
  fen: string;
  sideToMove: "white" | "black";
  score: EngineScore | null;
  winPercentWhite: number;
  bestMove: string | null;
  alternatives: number;
  depth: number;
  pv: string[];
  lines: EngineLineView[];
}

export interface MoveReview {
  ply: number;
  category: MoveCategory;
  winPercentBefore: number;
  winPercentAfter: number;
  loss: number;
  sacrifice: boolean;
}

export interface GameReport {
  accuracyWhite: number;
  accuracyBlack: number;
  counts: Record<MoveCategory, number>;
  worst: MoveReview | null;
  longestLoss: number;
}

export const START_FEN =
  "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";

function sideToMoveOf(fen: string): "white" | "black" {
  return fen.split(" ")[1] === "b" ? "black" : "white";
}

function whiteWinPercent(score: EngineScore | null, sideToMove: "white" | "black"): number {
  if (!score) return 50;
  const asWhite = scoreToWinPercent(score, "white");
  return sideToMove === "white" ? asWhite : 100 - asWhite;
}

function moverWinPercent(whitePercent: number, mover: "white" | "black"): number {
  return mover === "white" ? whitePercent : 100 - whitePercent;
}

export function positionFens(game: ParsedGame): string[] {
  const fens = [game.moves[0]?.fenBefore ?? START_FEN];
  for (const move of game.moves) fens.push(move.fenAfter);
  return fens;
}

export function toPositionAnalysis(
  ply: number,
  fen: string,
  result: EngineResult,
): PositionAnalysis {
  const sideToMove = sideToMoveOf(fen);
  const best = result.line;
  const score = best?.score ?? null;
  const whitePercent = whiteWinPercent(score, sideToMove);

  let alternatives = 0;
  if (best?.score && result.lines.length > 1) {
    const bestPercent = moverWinPercent(whiteWinPercent(best.score, sideToMove), sideToMove);
    alternatives = result.lines.filter((line) => {
      if (!line.score) return false;
      const percent = moverWinPercent(whiteWinPercent(line.score, sideToMove), sideToMove);
      return bestPercent - percent <= 10;
    }).length;
  }

  const lines: EngineLineView[] = result.lines.map((line) => ({
    san: uciLineToSan(fen, line.pv ?? []),
    score: line.score,
    depth: line.depth,
  }));

  return {
    ply,
    fen,
    sideToMove,
    score,
    winPercentWhite: whitePercent,
    bestMove: result.bestMove,
    alternatives,
    depth: best?.depth ?? 0,
    pv: uciLineToSan(fen, best?.pv ?? []),
    lines,
  };
}

export function computeReviews(game: ParsedGame, positions: PositionAnalysis[]): MoveReview[] {
  const reviews: MoveReview[] = [];
  const byPly = new Map(positions.map((position) => [position.ply, position]));

  for (const move of game.moves) {
    const before = byPly.get(move.ply);
    const after = byPly.get(move.ply + 1);
    if (!before || !after) continue;

    const mover = move.color;
    const winPercentBefore = moverWinPercent(before.winPercentWhite, mover);
    const winPercentAfter = moverWinPercent(after.winPercentWhite, mover);
    const isBestMove = before.bestMove === move.uci;
    const isOnlyMove = before.alternatives > 0 && before.alternatives <= 1;
    const sacrifice = isSacrifice(before.fen, after.fen, mover);

    const category = classifyMove({
      bestWinPercent: winPercentBefore,
      playedWinPercent: winPercentAfter,
      isBestMove,
      isOnlyMove,
      isSacrifice: sacrifice,
    });

    reviews.push({
      ply: move.ply,
      category,
      winPercentBefore,
      winPercentAfter,
      loss: Math.max(0, winPercentBefore - winPercentAfter),
      sacrifice,
    });
  }

  return reviews;
}

export function computeReport(game: ParsedGame, reviews: MoveReview[]): GameReport {
  const counts: Record<MoveCategory, number> = {
    brilliant: 0,
    great: 0,
    best: 0,
    inaccuracy: 0,
    miss: 0,
    blunder: 0,
  };

  const whiteSequence: number[] = [];
  const blackSequence: number[] = [];
  let worst: MoveReview | null = null;

  for (const review of reviews) {
    counts[review.category] += 1;
    const move = game.moves[review.ply];
    if (!move) continue;
    const sequence = move.color === "white" ? whiteSequence : blackSequence;
    sequence.push(review.winPercentBefore, review.winPercentAfter);
    if (!worst || review.loss > worst.loss) worst = review;
  }

  return {
    accuracyWhite: accuracyFromWinPercents(whiteSequence),
    accuracyBlack: accuracyFromWinPercents(blackSequence),
    counts,
    worst,
    longestLoss: worst?.loss ?? 0,
  };
}
